import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { X } from 'lucide-react';

// Wide enough for the report's tables to lay out as they do on a desktop, so
// the capture is not squeezed into a narrow column.
const PRINT_WIDTH_PX = 1120;
// Chrome refuses a canvas past this on either axis, and past roughly 256M
// pixels of area; exceeding either yields a blank or truncated capture, which
// is what silently dropped the tail of a long report.
const MAX_CANVAS_EDGE_PX = 16384;
const MAX_CANVAS_AREA_PX = 240000000;

/**
 * html2canvas captures a node exactly as it sits on screen, and the report is
 * full of things that clip: every wide table sits in a horizontal scroller and
 * the preview itself is a scroll area. Copy the content into a detached holder
 * that is laid out at full size with nothing clipped, and capture that instead.
 */
const buildPrintableClone = (source) => {
  const holder = document.createElement('div');
  holder.style.position = 'fixed';
  holder.style.top = '0';
  holder.style.left = '-100000px';
  holder.style.width = 'max-content';
  holder.style.minWidth = `${PRINT_WIDTH_PX}px`;
  holder.style.maxWidth = 'none';
  holder.style.backgroundColor = '#ffffff';
  holder.style.padding = '24px';

  holder.appendChild(source.cloneNode(true));
  document.body.appendChild(holder);

  // Undo every clip the on-screen layout relies on, so the capture sees the
  // full width of each table and the full height of the report.
  holder.querySelectorAll('*').forEach((node) => {
    node.style.overflow = 'visible';
    node.style.maxHeight = 'none';
  });

  return holder;
};

const resolveScale = (widthPx, heightPx) => {
  if (!widthPx || !heightPx) return 1;
  return Math.min(
    2,
    MAX_CANVAS_EDGE_PX / widthPx,
    MAX_CANVAS_EDGE_PX / heightPx,
    Math.sqrt(MAX_CANVAS_AREA_PX / (widthPx * heightPx))
  );
};

export default function PagePrintPreview({
  data,
  contentSelector,
  onClose,
  reportType = 'POS-Performance',
}) {
  const previewRef = useRef(null);

  useEffect(() => {
    const el = document.querySelector(contentSelector);
    if (el && previewRef.current) {
      // Clone content for preview (styles come from app stylesheet)
      previewRef.current.innerHTML = el.innerHTML;
    }
  }, [contentSelector]);

  const buildFileName = () => {
    const businessName = (
      data.businessInfo?.businessTitle || 'Business'
    ).toString();
    const startDate =
      data.dateRange?.startDate || new Date().toISOString().slice(0, 10);
    const endDate = data.dateRange?.endDate || startDate;
    const startTime = (data.filters?.startTime || '').replace(':', '-');
    const endTime = (data.filters?.endTime || '').replace(':', '-');
    const dateRangeStr =
      startTime && endTime
        ? `${startDate}_${startTime}-to-${endDate}_${endTime}`
        : `${startDate}-to-${endDate}`;
    const sanitize = (s) =>
      s
        .replace(/[^a-z0-9-_]+/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    return `${sanitize(businessName)}_${reportType}_${sanitize(dateRangeStr)}`;
  };

  const handlePrint = () => {
    const container = previewRef.current;
    if (!container) return;

    const printWindow = window.open('', '_blank', 'width=1024,height=768');
    if (!printWindow) return;

    const headHTML = document.head.innerHTML;
    const html = `<!doctype html><html><head>${headHTML}<title>Print Preview</title>
      <style>@page{margin:0.5in;}
      /* The report's tables live in horizontal scrollers, which the printer
         would clip to the page instead of scrolling. */
      *{overflow:visible !important;max-height:none !important;}</style></head>
      <body>${container.innerHTML}
      <script>window.onload=function(){window.print();setTimeout(()=>window.close(),300);}</script>
      </body></html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownloadPdf = async () => {
    const container = previewRef.current;
    if (!container) return;

    const [{ default: html2canvas }, jsPDFModule] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);

    // Letter size dimensions
    const pageWidthMm = 215.9;
    const pageHeightMm = 279.4;
    const marginMm = 12.7; // 0.5 inch margins
    const contentWidthMm = pageWidthMm - marginMm * 2;
    const contentHeightMm = pageHeightMm - marginMm * 2;

    const holder = buildPrintableClone(container);

    let canvas;
    try {
      const sourceWidth = holder.scrollWidth;
      const sourceHeight = holder.scrollHeight;

      canvas = await html2canvas(holder, {
        scale: resolveScale(sourceWidth, sourceHeight),
        backgroundColor: '#ffffff',
        useCORS: true,
        // Without an explicit window the capture is cropped to the real
        // viewport, which is what cut everything below the fold.
        windowWidth: sourceWidth,
        windowHeight: sourceHeight,
      });
    } finally {
      holder.remove();
    }

    if (!canvas?.width || !canvas?.height) return;

    const jsPDF = jsPDFModule.jsPDF || jsPDFModule.default;
    const pdf = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'p' });

    // Calculate dimensions
    const pxPerMm = canvas.width / contentWidthMm;
    const pageContentHeightPx = contentHeightMm * pxPerMm;

    let currentY = 0;
    let pageIndex = 0;

    // Split content across pages
    while (currentY < canvas.height) {
      const remainingHeight = canvas.height - currentY;
      const segmentHeight = Math.min(remainingHeight, pageContentHeightPx);

      // Create canvas segment
      const segmentCanvas = document.createElement('canvas');
      segmentCanvas.width = canvas.width;
      segmentCanvas.height = Math.max(1, Math.floor(segmentHeight));
      const ctx = segmentCanvas.getContext('2d');

      ctx.drawImage(
        canvas,
        0,
        Math.floor(currentY),
        canvas.width,
        Math.floor(segmentHeight),
        0,
        0,
        canvas.width,
        Math.floor(segmentHeight)
      );

      const imgData = segmentCanvas.toDataURL('image/jpeg', 0.95);
      const segmentHeightMm = segmentHeight / pxPerMm;

      if (pageIndex > 0) {
        pdf.addPage('letter', 'p');
      }

      pdf.addImage(
        imgData,
        'JPEG',
        marginMm,
        marginMm,
        contentWidthMm,
        Math.min(segmentHeightMm, contentHeightMm),
        undefined,
        'FAST'
      );

      currentY += segmentHeight;
      pageIndex++;
    }

    pdf.save(`${buildFileName()}.pdf`);
  };

  // Portalled to the body because the reporting pages sit inside containers
  // that establish their own containing block, which pinned the overlay to the
  // content area and tucked its top under the app header. The z-index clears
  // MUI's own layers, which the app chrome is built from.
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 thermal-print-modal"
      style={{ zIndex: 1500 }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col thermal-print-container">
        {/* Header */}
        <div className="no-print flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Print Preview</h2>
          <div className="flex gap-2">
            <Button onClick={handleDownloadPdf} variant="secondary" size="sm">
              Download PDF
            </Button>
            <Button onClick={handlePrint} variant="default" size="sm">
              Print
            </Button>
            <Button onClick={onClose} variant="outline" size="sm">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-auto p-4 bg-gray-50">
          <div className="bg-white mx-auto shadow-sm p-6 max-w-5xl w-full">
            <div ref={previewRef} />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
